import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface PreloadState {
  [routePath: string]: {
    isPreloaded: boolean;
    isLoading: boolean;
    priority: 'high' | 'medium' | 'low';
    loadedAt?: number;
  };
}

interface PreloadContextType {
  preloadState: PreloadState;
  startPreloading: () => void;
  cancelPreloading: () => void;
  isPreloading: boolean;
  preloadProgress: number;
  isRoutePreloaded: (routePath: string) => boolean;
  preloadDashboardData: (profileId: string) => Promise<void>;
  isDataPreloaded: boolean;
}

const PreloadContext = createContext<PreloadContextType | undefined>(undefined);

// Route → import map (only the actual page, not lazy wrappers)
const ROUTE_IMPORTS: Record<string, () => Promise<unknown>> = {
  '/dashboard': () => import('@/pages/Dashboard'),
  '/transacoes': () => import('@/pages/Transactions'),
  '/lancamentos': () => import('@/pages/ReleasesV2'),
  '/cartoes': () => import('@/pages/Cards'),
  '/contas': () => import('@/pages/Accounts'),
  '/categorias': () => import('@/pages/Categories'),
  '/metas': () => import('@/pages/Goals'),
  '/agenda': () => import('@/pages/Agenda'),
  '/relatorios': () => import('@/pages/Reports'),
  '/orcamentos': () => import('@/pages/Budgets'),
  '/configuracoes': () => import('@/pages/Settings'),
  '/ajuda-e-suporte': () => import('@/pages/HelpSupport'),
  '/documentacao': () => import('@/pages/Documentation'),
  '/admin': () => import('@/pages/Admin'),
};

// Priority and delay configuration
const ROUTE_CONFIG: Record<string, { priority: 'high' | 'medium' | 'low'; delay: number }> = {
  '/dashboard': { priority: 'high', delay: 0 },
  '/transacoes': { priority: 'high', delay: 0 },
  '/lancamentos': { priority: 'high', delay: 0 },
  '/contas': { priority: 'medium', delay: 0 },
  '/cartoes': { priority: 'medium', delay: 0 },
  '/categorias': { priority: 'medium', delay: 0 },
  '/metas': { priority: 'medium', delay: 0 },
  '/agenda': { priority: 'medium', delay: 0 },
  '/relatorios': { priority: 'low', delay: 50 },
  '/orcamentos': { priority: 'low', delay: 50 },
  '/configuracoes': { priority: 'low', delay: 100 },
  '/ajuda-e-suporte': { priority: 'low', delay: 100 },
  '/documentacao': { priority: 'low', delay: 150 },
  '/admin': { priority: 'low', delay: 150 },
};

export const PreloadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preloadState, setPreloadState] = useState<PreloadState>(() => {
    const initialState: PreloadState = {};
    Object.entries(ROUTE_CONFIG).forEach(([route, config]) => {
      initialState[route] = {
        isPreloaded: false,
        isLoading: false,
        priority: config.priority,
      };
    });
    return initialState;
  });
  
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const preloadRoute = useCallback(async (routePath: string) => {
    if (abortControllerRef.current?.signal.aborted) return;
    
    let shouldProceed = false;
    setPreloadState(prev => {
      const routeState = prev[routePath];
      if (routeState?.isPreloaded || routeState?.isLoading) {
        shouldProceed = false;
        return prev;
      }
      shouldProceed = true;
      return { ...prev, [routePath]: { ...prev[routePath], isLoading: true } };
    });

    if (!shouldProceed) return;

    try {
      const startTime = performance.now();
      const importFn = ROUTE_IMPORTS[routePath];
      if (importFn) {
        await importFn();
      }
      
      if (import.meta.env.DEV) {
        console.log(`[Preload] ${routePath} in ${(performance.now() - startTime).toFixed(0)}ms`);
      }

      if (abortControllerRef.current?.signal.aborted) return;

      setPreloadState(prev => ({
        ...prev,
        [routePath]: { ...prev[routePath], isPreloaded: true, isLoading: false, loadedAt: Date.now() },
      }));
    } catch (error) {
      if (abortControllerRef.current?.signal.aborted) return;
      if (import.meta.env.DEV) {
        console.warn(`[Preload] Failed: ${routePath}`, error);
      }
      setPreloadState(prev => ({
        ...prev,
        [routePath]: { ...prev[routePath], isLoading: false },
      }));
    }
  }, []);

  const startPreloading = useCallback(() => {
    if (isPreloading) return;
    
    if ('connection' in navigator && (navigator as any).connection?.saveData) {
      if (import.meta.env.DEV) console.log('[Preload] Save data mode, skipping');
      return;
    }

    setIsPreloading(true);
    setPreloadProgress(0);
    abortControllerRef.current = new AbortController();
    
    const routes = Object.entries(ROUTE_CONFIG);
    let completedRoutes = 0;
    
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current = [];
    
    routes.forEach(([route, config]) => {
      const timeout = setTimeout(() => {
        if (abortControllerRef.current?.signal.aborted) return;
        
        const executePreload = () => {
          preloadRoute(route).finally(() => {
            completedRoutes++;
            setPreloadProgress((completedRoutes / routes.length) * 100);
            if (completedRoutes === routes.length) {
              setIsPreloading(false);
            }
          });
        };

        if ('requestIdleCallback' in window) {
          requestIdleCallback(executePreload, { timeout: 1000 });
        } else {
          executePreload();
        }
      }, config.delay);
      
      timeoutsRef.current.push(timeout);
    });
  }, [isPreloading, preloadRoute]);

  const cancelPreloading = useCallback(() => {
    abortControllerRef.current?.abort();
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current = [];
    setIsPreloading(false);
    setPreloadProgress(0);
    setPreloadState(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(route => {
        if (newState[route].isLoading) {
          newState[route] = { ...newState[route], isLoading: false };
        }
      });
      return newState;
    });
  }, []);

  const isRoutePreloaded = useCallback((routePath: string) => {
    return preloadState[routePath]?.isPreloaded || false;
  }, [preloadState]);

  // Data preloading
  const [isDataPreloaded, setIsDataPreloaded] = useState(false);
  const queryClient = useQueryClient();

  const preloadDashboardData = useCallback(async (profileId: string) => {
    if (isDataPreloaded) return;

    try {
      if (import.meta.env.DEV) {
        console.log('[PreloadContext] Preloading dashboard data...');
      }
      const startTime = performance.now();

      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: ['transactions-preload', profileId],
          queryFn: async () => {
            const { data } = await supabase
              .from('transactions')
              .select('id, date, type, amount, status, category_id')
              .eq('profile_id', profileId)
              .order('date', { ascending: false })
              .limit(50);
            return data || [];
          },
          staleTime: 30 * 1000,
        }),
        queryClient.prefetchQuery({
          queryKey: ['categories-preload', profileId],
          queryFn: async () => {
            const { data } = await supabase
              .from('categories')
              .select('id, name, color, icon, type')
              .eq('profile_id', profileId);
            return data || [];
          },
          staleTime: 5 * 60 * 1000,
        }),
        queryClient.prefetchQuery({
          queryKey: ['goals-preload', profileId],
          queryFn: async () => {
            const { data } = await supabase
              .from('goals')
              .select('id, name, target_amount, current_amount, deadline')
              .eq('profile_id', profileId)
              .neq('status', 'completed')
              .limit(3);
            return data || [];
          },
          staleTime: 2 * 60 * 1000,
        }),
      ]);

      setIsDataPreloaded(true);
      
      if (import.meta.env.DEV) {
        console.log(`[PreloadContext] Dashboard data preloaded in ${(performance.now() - startTime).toFixed(0)}ms`);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[PreloadContext] Data preload failed:', error);
      }
    }
  }, [isDataPreloaded, queryClient]);

  const value = {
    preloadState,
    startPreloading,
    cancelPreloading,
    isPreloading,
    preloadProgress,
    isRoutePreloaded,
    preloadDashboardData,
    isDataPreloaded,
  };

  return (
    <PreloadContext.Provider value={value}>
      {children}
    </PreloadContext.Provider>
  );
};

export const usePreload = () => {
  const context = useContext(PreloadContext);
  if (context === undefined) {
    throw new Error('usePreload must be used within a PreloadProvider');
  }
  return context;
};
